import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Restricted, SingleSelect } from '@/common';
import { Search } from '@/components/common/Search';
import { Button, IconButton, Label, LABEL_TYPE } from '@/components/generic';
import {
  BUTTON_STYLES,
  ButtonVariant,
  DEFAULT_BUTTON_STYLES
} from '@/components/generic/Button/button-config';
import { IconButtonVariant } from '@/components/generic/IconButton/icon-button-config';
import { queryClient } from '@/configuration/react-query';
import { AuthContext } from '@/contexts';
import { COLORS, LANGUAGE_CODE } from '@/enums';
import { getCamelCase, getLanguageOptions } from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import i18n from '@/i18n';
import { BlueDownloadIcon, FilterIcon, PlusIcons } from '@/resources';
import { useUpdateUserLanguageMutation } from '@/services';
import { getTestId, TestIds } from '@/shared/test-ids';
import { SelectOptionProps } from '@/types';

type TableHeaderProps = {
  tableTitle: string;
  addButtonLabel: string;
  createPermission?: string;
  search?: (query: string) => void;
  handleCreate: () => void;
  handleOpenFilterModal?: () => void;
  numberOfAppliedFilters: number;
  filterButtonRef?: React.Ref<HTMLDivElement>;
  testIdPrefix?: string;
  downloadButtonLabel?: string;
  downloadUrl?: string;
};
export const TableHeader = ({
  tableTitle,
  addButtonLabel,
  createPermission,
  search,
  handleCreate,
  handleOpenFilterModal,
  numberOfAppliedFilters,
  filterButtonRef,
  testIdPrefix,
  downloadButtonLabel,
  downloadUrl
}: TableHeaderProps) => {
  const [t] = useTranslation('common');
  const [language, setLanguage] = useState<SelectOptionProps>();
  const { currentUserData: currentUser } = useContext(AuthContext);

  useEffect(() => {
    if (
      currentUser &&
      localStorage.getItem('currentLanguage') !== LANGUAGE_CODE[currentUser.currentLanguage]
    ) {
      localStorage.setItem('currentLanguage', LANGUAGE_CODE[currentUser.currentLanguage]);
    }
    currentUser?.currentLanguage &&
      setLanguage({
        value: LANGUAGE_CODE[currentUser.currentLanguage],
        label: t(getCamelCase(currentUser.currentLanguage.toString()))
      });
  }, [currentUser]);

  const handleChange = (v: SelectOptionProps) => {
    setLanguage(v);
    updateUserLanguage({
      id: Number(currentUser?.id),
      currentLanguage: v?.value as string
    });
    i18n.changeLanguage(LANGUAGE_CODE[v?.value as number]);
    localStorage.setItem('currentLanguage', v?.value as string);
  };

  const { mutate: updateUserLanguage } = useUpdateUserLanguageMutation(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('userUpdated'));
    },
    onError: (error) => {
      showToast(ToastType.ERROR, t(`${error.response.data.ErrorMessages[0].DisplayMessage}`));
    }
  });

  const prefix = testIdPrefix || TestIds.common.tablePrefix;

  return (
    <div
      data-testid={getTestId(prefix, 'header')}
      className='sticky right-0 top-0 z-20 flex w-full flex-col-reverse justify-between rounded md:flex-row md:items-center md:px-[35px] md:py-5'>
      <div data-testid={getTestId(prefix, 'title')}>
        <Label text={tableTitle} type={LABEL_TYPE.H1} className='left-0 my-3 md:mt-0' />
      </div>
      <div className='-mx-3 flex items-center pb-3 shadow-[0px_2px_6px_0px_rgba(40,73,134,0.20);] md:mx-0 md:pb-0 md:shadow-none'>
        {search && (
          <div className='m-[15px] w-full md:w-80'>
            <Search callback={search} testId={getTestId(prefix, 'search-input')} />
          </div>
        )}
        {handleOpenFilterModal && (
          <div className='mr-4'>
            <div ref={filterButtonRef || null} className='relative size-10'>
              <IconButton
                dataTestId={getTestId(prefix, 'filter-button')}
                className='size-full'
                variant={IconButtonVariant.PRIMARY}
                handleOnClick={handleOpenFilterModal}>
                <FilterIcon className='size-5' />
              </IconButton>
              {numberOfAppliedFilters > 0 && (
                <div className='absolute right-0 top-1 flex size-5 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full bg-primary-blue text-primary-white'>
                  <Label text={numberOfAppliedFilters} type={LABEL_TYPE.PARAGRAPH2} />
                </div>
              )}
            </div>
          </div>
        )}
        {/* Shares the create gate: whoever may add a device may fetch its installer. */}
        {downloadUrl && createPermission && (
          <Restricted to={[createPermission]}>
            <a
              data-testid={getTestId(prefix, 'download-button')}
              href={downloadUrl}
              download
              className={`${DEFAULT_BUTTON_STYLES} ${BUTTON_STYLES[ButtonVariant.SECONDARY]} mr-[15px] w-10 truncate !px-0 sm:!px-4 md:w-auto`}>
              <BlueDownloadIcon className='size-4 md:mr-2' />
              <div className='hidden md:block'>{downloadButtonLabel}</div>
            </a>
          </Restricted>
        )}
        {createPermission && (
          <Restricted to={[createPermission]}>
            <div className='mr-[15px] md:flex'>
              <Button
                dataTestId={getTestId(prefix, 'create-button')}
                handleOnClick={handleCreate}
                variant={ButtonVariant.PRIMARY}
                className='w-10 truncate !px-0 sm:!px-6 md:mr-auto md:w-auto'>
                <PlusIcons className='size-3.5 md:mr-2' fill='white' />
                <div className='hidden md:block'>{addButtonLabel}</div>
              </Button>
            </div>
          </Restricted>
        )}
        <div id='dropdown' className='hidden h-10 items-center md:flex'>
          <SingleSelect
            id='current-language'
            options={getLanguageOptions()}
            handleChange={(value) => handleChange(value as SelectOptionProps)}
            value={language}
            borderStyle={`1px solid ${COLORS.PRIMARY_BLUE}`}
          />
        </div>
      </div>
    </div>
  );
};
